import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { SignOut, Check, X } from "@phosphor-icons/react";

function fmtDateTime(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * PendingDropsPanel — Org-wide visibility of pending shift-drop requests.
 * Supervisors / admins get Approve / Deny; volunteers + logistics see
 * read-only cards. Approval releases the shift back to the open pool.
 */
export default function PendingDropsPanel() {
  const { user } = useAuth();
  const canDecide = isSupervisorOrAbove(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/tours/drop-requests");
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pending drops");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function approve(t) {
    try {
      await api.post(`/tours/${t.id}/drop-approve`);
      toast.success("Drop approved — shift is back in the open pool");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
  }
  async function deny(t) {
    try {
      await api.post(`/tours/${t.id}/drop-deny`);
      toast.success("Drop denied — member remains on the shift");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Deny failed"); }
  }

  return (
    <div className="space-y-4" data-testid="pending-drops-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {canDecide
            ? "Volunteers asking to be released from an assigned shift. Approving puts the shift back in the open pool."
            : "Live drop requests across the corps. Only supervisors can approve or deny."}
        </p>
        <span className="tab-tag text-slate-500" data-testid="drops-count">{rows.length} pending</span>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 italic p-6 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="surface-grid p-8 text-center text-sm text-slate-500" data-testid="drops-empty">
          <SignOut size={28} weight="bold" className="mx-auto mb-2 text-slate-400" />
          No pending drop requests.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((t) => {
            const dr = t.drop_request || {};
            const isMine = user && dr.requested_by === user.id;
            return (
              <div key={t.id} className={`surface-grid p-4 ${isMine ? "ring-2 ring-emerald-500" : ""}`} data-testid={`drop-${t.id}`}>
                <div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-3">
                  <div>
                    <div className="font-display font-bold text-sm">
                      {dr.requested_by_name || t.assigned_user_name || "Member"} wants to drop
                      {isMine && <span className="ml-2 text-emerald-700 text-[10px] font-bold tracking-wider">★ YOUR REQUEST</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 mono">
                      Requested {dr.requested_at ? new Date(dr.requested_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <Badge className="rounded-sm bg-amber-200 text-amber-900 text-[10px]">PENDING</Badge>
                </div>

                <div className="border border-amber-200 bg-amber-50 rounded-sm p-2 text-sm">
                  <div className="tab-tag text-slate-500 mb-1">Shift</div>
                  <div className="font-semibold">{t.position || t.title || "Shift"}</div>
                  <div className="text-xs text-slate-700 mono">{fmtDateTime(t.start_time)}</div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <Badge className="rounded-sm text-[10px] bg-slate-700 text-white">{t.required_certification}</Badge>
                    {t.ambulance_role && <Badge className="rounded-sm text-[10px] bg-teal-700 text-white">{t.ambulance_role}</Badge>}
                  </div>
                </div>

                {dr.reason && (
                  <div className="mt-3 text-xs border-l-2 border-slate-400 pl-2 italic text-slate-600">
                    "{dr.reason}"
                  </div>
                )}

                {canDecide ? (
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-200">
                    <Button size="sm" onClick={() => approve(t)} className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`drop-approve-${t.id}`}>
                      <Check size={14} weight="bold" className="mr-1" />Approve Drop
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deny(t)} className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid={`drop-deny-${t.id}`}>
                      <X size={14} weight="bold" className="mr-1" />Deny
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3 pt-3 border-t border-slate-200 text-[11px] text-slate-500 italic text-center">
                    Awaiting supervisor decision.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
